import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React, { useCallback, useState } from 'react';
import { useAppDispatch } from '../../../redux/hooks';
import {
  type FormBuilderField,
  type FormBuilderFormGroup,
  moveFormFieldBetweenGroups,
  reorderFormFields,
  reorderFormGroups,
} from '../../../redux/slices/form-builder-slice';
import FormBuilderDynamicField from '../fields/form-builder-dynamic-field.component';

interface DnDFormBuilderProps {
  children: React.ReactNode;
  fieldGroups: FormBuilderFormGroup[];
}

const DnDFormBuilder = ({ children, fieldGroups }: DnDFormBuilderProps) => {
  const dispatch = useAppDispatch();
  const [activeField, setActiveField] = useState<FormBuilderField | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // user needs to move cursor 10 pixels before drag starts
        distance: 10,
      },
    })
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      const isDraggingField = active.data.current?.type === 'field';
      const draggableId = active.id;

      if (!isDraggingField || !draggableId) {
        return;
      }
      const field = fieldGroups
        .flatMap((group) => group.fields || [])
        .find((field) => {
          return (field.id ?? field.uuid) === draggableId;
        });

      if (field) {
        setActiveField(field);
      }
    },
    [fieldGroups]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveField(null);
      if (!over || active.id === over.id) return;

      const isDraggingGroup = active.data?.current?.type === 'group';
      const isDraggingField = active.data?.current?.type === 'field';

      const isOverField = over.data?.current?.type === 'field';
      const isOverGroup = over.data?.current?.type === 'group';

      const sourceId = active.id;
      const targetId = over.id;

      // Moving field within or between groups
      if (isDraggingField && (isOverField || isOverGroup)) {
        const sourceGroupId = active.data.current?.groupId;
        const targetGroupId = over.data.current?.groupId;
        if (sourceGroupId === targetGroupId) {
          // Same section - reorder fields
          dispatch(
            reorderFormFields({
              groupId: sourceGroupId,
              droppedItem: sourceId,
              overItem: targetId,
            })
          );
        } else {
          // Different sections - move field
          dispatch(
            moveFormFieldBetweenGroups({
              fieldId: sourceId,
              sourceGroupId,
              targetGroupId,
              overFieldId: isOverGroup ? undefined : targetId,
            })
          );
        }
      }

      if (isDraggingGroup && isOverGroup) {
        // Group reordering
        dispatch(
          reorderFormGroups({
            droppedItem: sourceId,
            overItem: targetId,
          })
        );
      }
    },
    [dispatch]
  );

  // Group items for sortable context
  const items = fieldGroups.map((group) => (group.id ?? group.uuid)!);
  return (
    <DndContext
      id="form-builder-dnd-context"
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
      {activeField ? (
        <DragOverlay>
          <div className="opacity-90">
            <FormBuilderDynamicField field={activeField} className="cursor-grabbing" />
          </div>
        </DragOverlay>
      ) : null}
    </DndContext>
  );
};

export default DnDFormBuilder;
